/* eslint-disable max-lines */
/* eslint-disable no-nested-ternary */
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import { Spacings, mediaQuery } from '@styles/index';
import PreCheckout from '@cont/Checkout/PreCheckout';
import Layout from '@layout/PaymentLayout/PaymentLayout';
import {
  fetchGoalHomeSubscriptionData,
  fetchGoalInfo,
  fetchGoalSubscriptionFeatures,
  fetchPriceHikeInfo
} from '@api/fetchers/goal';
import {
  fetchBundleDetails,
  fetchCheckoutStaticData,
  fetchComboFeatures,
  fetchPricings,
  fetchSubscriptionFaqs,
  fetchSubscriptionFeatures,
  fetchSubscriptionHighlights,
  getPricingsEndpoint
} from '@api/fetchers/checkout';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro,
  getIsSubscribedToLite
} from '@api/selectors/subscription';
import usePendingPaymentStatus from '@layout/PaymentLayout/usePendingPaymentStatus';
import { useCallback, useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import pushRoute from '@utils/pushRoute';
import useReferralTracking from '@cont/Checkout/useReferralTracking';
import { getParameterByName } from '@utils/textHelper';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import { segment } from '@actions/analytics';
import { CHECKOUT_SUBSCRIPTION_TYPE_SELECTED } from '@cont/Checkout/constants/segment';
import { getSubscriptionUpgradeFlags } from '@cont/Checkout/utils';
import { getSubscriptionTypeLabelFromPriceMode } from '@utils/subscriptionHelpers/getSubscriptionTypeForAnalytics';
import { SWRConfig } from 'swr';
import getPreservedQueryParams from '@utils/getPreservedQueryParams';
import Dynamic from '@base/Dynamic/Dynamic';
import { NextSeo } from 'next-seo';
import { subscriptionTypes } from '@constants/index';
import { getSubsriptionFeaturesKey } from '@api/hooks/goal/useSubscriptionBenefits';
import getSubscriptionCmsKey from '@utils/subscriptionHelpers/getSubscriptionCmsKey';

const PreCheckoutBundle = Dynamic(
  () => import('@cont/Checkout/PreCheckoutBundle')
);
const Checkout = Dynamic(() => import('@cont/Checkout/Checkout'));

const PricingPage = ({
  goalDetails,
  bundleDetails,
  subscriptionData,
  isLiteAvailable,
  isIconicProAvailable,
  comboFeatures,
  subscriptionFaqs,
  subscriptionFeatures,
  subscriptionComparisonFeatures,
  pricings,
  checkoutStaticData,
  priceHikeInfo
}) => {
  const router = useRouter();
  const { query, push } = router;
  const {
    goalUID,
    goalSlug,
    referral_code = '',
    is_iconic,
    source = ''
  } = query;

  const isAppSource = source === 'app';

  const isIconic =
    is_iconic || getParameterByName('is_iconic', router.asPath) === 'true';
  const plan_type = getParameterByName('plan_type', router.asPath);
  const show_pricing = getParameterByName('show_pricing', router.asPath);
  const deeplink = getParameterByName('deeplink', router.asPath);
  const banner = getParameterByName('banner', router.asPath);

  const isCombo = !!bundleDetails?.topologies;
  const isIconicAvailable = !!goalDetails.isIconicAvailable;

  const isIconicUser = useMyInfo(getIsSubscribedToIconic(goalUID));
  const isIconicProUser = useMyInfo(getIsSubscribedToIconicPro(goalUID));
  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));
  const isLiteActive = useMyInfo(getIsSubscribedToLite(goalUID));

  const { isIconicUpgrade, isPlusIconicProUpgrade, isIconicProUpgrade } =
    getSubscriptionUpgradeFlags({
      isPlusUser,
      isIconicUser,
      isIconicProUser,
      isIconicAvailable: goalDetails?.isIconicAvailable,
      isIconicProAvailable: goalDetails?.isIconicProAvailable
    });

  const { pendingPaymentStatus } = usePendingPaymentStatus(goalUID);

  const goalName = goalDetails?.name;
  const metaTitle = `${goalName} Subscription | Unacademy `;
  const metaDescription = `Subscribe for ${goalName} live classes, full syllabus coverage, test series, study materials and expert mentorship. Start your preparation with Unacademy today.`;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/subscribe`;

  const showPlans = useCallback(
    (planType = 'plus') => {
      if (pendingPaymentStatus && !isEmpty(pendingPaymentStatus.clashDetails)) {
        pushRoute(
          `/goal/[goalSlug]/[goalUID]/subscribe${getPreservedQueryParams()}`,
          `/goal/-/${
            pendingPaymentStatus.clashDetails.uid
          }/subscribe${getPreservedQueryParams()}`
        );
      } else if (
        (pendingPaymentStatus?.type === 'PIP' &&
          pendingPaymentStatus?.data?.isActivePartSet) ||
        pendingPaymentStatus?.type === 'PAC'
      ) {
        pushRoute(
          `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${getPreservedQueryParams()}`,
          `/goal/${goalSlug}/${goalUID}/subscribe/${
            pendingPaymentStatus.data.subscriptionUid
          }${getPreservedQueryParams()}`
        );
      } else {
        pushRoute(
          `/goal/[goalSlug]/[goalUID]/subscribe${getPreservedQueryParams({
            plan_type: planType,
            referral_code,
            deeplink,
            banner
          })}`,
          `/goal/${goalSlug}/${goalUID}/subscribe${getPreservedQueryParams({
            plan_type: planType,
            referral_code,
            deeplink,
            banner
          })}`
        );
      }
    },
    [pendingPaymentStatus, goalSlug, goalUID, referral_code, deeplink, banner]
  );

  useEffect(() => {
    if (isAppSource) {
      pushRoute(
        `/goal/[goalSlug]/[goalUID]/subscribe${getPreservedQueryParams()}`,
        `/goal/${goalSlug}/${goalUID}/subscribe${getPreservedQueryParams({
          referral_code
        })}`,
        {},
        '',
        true
      );
    }
  }, [goalSlug, goalUID, isAppSource, referral_code]);

  useEffect(() => {
    if (
      isIconicUser &&
      isIconicAvailable &&
      plan_type !== 'iconic' &&
      plan_type !== 'iconic_pro' &&
      !isAppSource
    ) {
      pushRoute(
        `/goal/[goalSlug]/[goalUID]/subscribe${getPreservedQueryParams()}`,
        `/goal/${goalSlug}/${goalUID}/subscribe${getPreservedQueryParams({
          plan_type: 'iconic',
          referral_code
        })}`,
        {},
        '',
        true
      );
    }
  }, [
    isIconicAvailable,
    isIconicUser,
    referral_code,
    goalSlug,
    goalUID,
    plan_type,
    isAppSource
  ]);

  useReferralTracking();

  const showPlansScreen =
    (!isCombo &&
      !isIconicAvailable &&
      !isLiteAvailable &&
      !isIconicProAvailable) ||
    isIconic ||
    plan_type ||
    show_pricing;

  useEffect(() => {
    if (!showPlansScreen) return;

    if (pendingPaymentStatus && !isEmpty(pendingPaymentStatus.clashDetails)) {
      pushRoute(
        `/goal/[goalSlug]/[goalUID]/subscribe${getPreservedQueryParams()}`,
        `/goal/-/${
          pendingPaymentStatus.clashDetails.uid
        }/subscribe${getPreservedQueryParams()}`,
        {},
        '',
        true
      );
    } else if (
      pendingPaymentStatus?.type === 'PIP' &&
      pendingPaymentStatus?.data?.isActivePartSet
    ) {
      pushRoute(
        `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${getPreservedQueryParams()}`,
        `/goal/${goalSlug}/${goalUID}/subscribe/${
          pendingPaymentStatus.data.subscriptionUid
        }${getPreservedQueryParams()}`,
        {},
        '',
        true
      );
    } else if (pendingPaymentStatus?.type === 'PAC') {
      pushRoute(
        `/goal/[goalSlug]/[goalUID]/subscribe/[subscriptionUID]${getPreservedQueryParams()}`,
        `/goal/${goalSlug}/${goalUID}/subscribe/${
          pendingPaymentStatus.data.subscriptionUid
        }${getPreservedQueryParams()}`,
        {},
        '',
        true
      );
    }
  }, [pendingPaymentStatus, goalSlug, goalUID, showPlansScreen]);

  const getPriceMode = () => {
    if (plan_type) return plan_type;
    return isLiteActive ? 'lite' : isIconic === 'true' ? 'iconic' : 'plus';
  };

  const [priceMode, setPriceMode] = useState(getPriceMode());

  useEffect(() => {
    setPriceMode(getPriceMode());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan_type]);

  const eventProps = useCheckoutEventProps(goalUID);

  const togglePriceMode = (mode) => {
    setPriceMode(mode);
    segment.track(CHECKOUT_SUBSCRIPTION_TYPE_SELECTED, {
      ...eventProps,
      subscription_type: getSubscriptionTypeLabelFromPriceMode(mode)
    });
    const queryParams = new URLSearchParams(
      router.asPath.includes('?') ? router.asPath.split('?')[1] : ''
    );
    queryParams.set('plan_type', mode);
    const queryString = queryParams.toString();
    pushRoute(
      `/goal/[goalSlug]/[goalUID]/subscribe${
        queryString ? `?${queryString}` : ''
      }`,
      `/goal/${goalSlug}/${goalUID}/subscribe${
        queryString ? `?${queryString}` : ''
      }`,
      { shallow: true },
      '',
      true,
      false
    );
  };

  return (
    <>
      <NextSeo
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription,
          url: canonicalURL
        }}
      />
      {showPlansScreen ? (
        <SWRConfig
          value={{
            fallback: {
              [getPricingsEndpoint(goalUID)]: pricings,
              'active-price-hikes': priceHikeInfo
            }
          }}
        >
          <Checkout
            // @ts-ignore
            goalUID={String(goalUID)}
            priceMode={priceMode}
            togglePriceMode={togglePriceMode}
            isIconicUpgrade={isIconicUpgrade}
            isPlusIconicProUpgrade={isPlusIconicProUpgrade}
            isIconicProUpgrade={isIconicProUpgrade}
            goalDetails={goalDetails}
            bundleDetails={bundleDetails}
            checkoutStaticData={checkoutStaticData}
          />
        </SWRConfig>
      ) : isCombo ? (
        <PreCheckoutBundle
          // @ts-ignore
          showPlans={showPlans}
          bundleDetails={bundleDetails}
          comboDetails={goalDetails}
          comboFeatures={comboFeatures}
          subscriptionFaqs={subscriptionFaqs}
        />
      ) : (
        <SWRConfig
          value={{
            fallback: {
              [getSubsriptionFeaturesKey(
                String(goalUID),
                isIconicProAvailable
              )]: subscriptionComparisonFeatures
            }
          }}
        >
          <PreCheckout
            showPlans={showPlans}
            goalData={goalDetails}
            lowestPriceMap={subscriptionData?.lowestPriceMap}
            subscriptionFeatures={subscriptionFeatures}
          />
        </SWRConfig>
      )}
    </>
  );
};

// @ts-ignore
PricingPage.pageInfo = {
  isMWebSupported: true,
  name: 'Subscription Plan Type Selection',
  layoutOptions: {
    isCheckoutUrl: true,
    hideFooter: true,
    disableDarkMode: true,
    hideIntercom: true,
    hideFreshbot: true
  }
};

const CustomLayout = styled(Layout)`
  & > div.container {
    grid-column-gap: ${Spacings.SPACING_4B};
    ${mediaQuery.uptoMobile} {
      grid-column-gap: 0px !important;
    }
  }
`;

// CDN cache with a TTL of 4hrs is enabled for this page
export const getStaticProps = async ({ params: query }) => {
  const { goalUID, goalSlug } = query;

  const [goalDetails, bundleDetails, subscriptionData] = await Promise.all([
    fetchGoalInfo(goalUID),
    fetchBundleDetails(goalUID),
    fetchGoalHomeSubscriptionData(goalUID)
  ]);

  const isCombo = !!bundleDetails?.topologies;
  const isIconicAvailable = !!goalDetails?.isIconicAvailable;
  const isLiteAvailable = subscriptionData?.lowestPriceMap?.liteLowestPrice > 0;
  const isIconicProAvailable = !!goalDetails?.isIconicProAvailable;

  const promises = [];

  promises.push(
    resolvePromiseToObject(
      fetchCheckoutStaticData(goalUID),
      'checkoutStaticData'
    )
  );
  promises.push(resolvePromiseToObject(fetchPriceHikeInfo(), 'priceHikeInfo'));
  promises.push(resolvePromiseToObject(fetchPricings(goalUID), 'pricings'));

  if (!isCombo) {
    promises.push(
      resolvePromiseToObject(
        fetchGoalSubscriptionFeatures(goalUID, isIconicProAvailable),
        'subscriptionComparisonFeatures'
      )
    );
  }

  if (isCombo) {
    const goalUIDs = bundleDetails.topologies?.map((goal) => goal.uid) || [];

    promises.push(
      resolvePromiseToObject(fetchComboFeatures(goalUIDs), 'comboFeatures')
    );
  }

  if (isIconicAvailable) {
    const externalApps = goalDetails.externalAppsAvailable;

    const iconicType = externalApps.length
      ? externalApps[0].name.toLowerCase()
      : getSubscriptionCmsKey(goalUID, isIconicProAvailable);

    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFeatures(iconicType),
        'subscriptionFeatures'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionHighlights(iconicType),
        'subscriptionHighlights'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFaqs(iconicType),
        'subscriptionFaqs'
      )
    );
  } else if (isIconicProAvailable) {
    const iconicProType = `iconic-pro-${goalUID}`;

    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFeatures(iconicProType),
        'subscriptionFeatures'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionHighlights(iconicProType),
        'subscriptionIconicProHighlights'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFaqs(iconicProType),
        'subscriptionIconicProFaqs'
      )
    );
  } else if (isLiteAvailable) {
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFeatures(goalUID),
        'subscriptionFeatures'
      )
    );
  } else if (isCombo) {
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFaqs('bundle'),
        'subscriptionFaqs'
      )
    );
  }

  if (isIconicProAvailable && isIconicAvailable) {
    const iconicProType = `iconic-pro-${goalUID}`;

    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFeatures(iconicProType),
        'subscriptionIconicProFeatures'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionHighlights(iconicProType),
        'subscriptionIconicProHighlights'
      )
    );
    promises.push(
      resolvePromiseToObject(
        fetchSubscriptionFaqs(iconicProType),
        'subscriptionIconicProFaqs'
      )
    );
  }

  let resolvedData = {};
  if (promises.length > 0)
    resolvedData = await Promise.all(promises).then((resolvedVals) => {
      const resultObj = {};
      for (let i = 0; i < resolvedVals.length; i += 1)
        resultObj[resolvedVals[i].type] = resolvedVals[i].result;
      return resultObj;
    });

  // @ts-ignore
  if (goalDetails?.isUaIconGoal && resolvedData.pricings?.subscriptions?.[0]) {
    return {
      redirect: {
        // @ts-ignore
        destination: `/goal/${goalSlug}/${goalUID}/subscribe/${resolvedData.pricings?.subscriptions?.[0].uid}`,
        permanent: false
      }
    };
  }

  return {
    props: {
      goalDetails,
      bundleDetails,
      subscriptionData,
      ...resolvedData,
      isLiteAvailable,
      isIconicProAvailable
    },
    revalidate: 1 * 60
  };
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

PricingPage.Layout = CustomLayout;

export default PricingPage;
